import {
  BASE_PRODUCTS_PRICING_QUERY_BFF,
  BASE_PRODUCTS_PRICING_QUERY_PRICING_SERVICE,
  BASE_PRODUCTS_RELATED_PRODUCTS_QUERY,
} from '~/builder/queries/grid';

import { getProductGqlQueryParameters } from './getProductGridQueryParameters';

/* according to Matt Mccabe from the API team, this particular endpoint treats code 3930 and 3925 as vip,
 all other codes are treated as lead, see full doc of the codes: https://confluence.techstyle.net/display/API/Status+Codes.+ALL+OF+THEM */
export const MEMBERSHIP_STATUS = {
  VIP: 3930,
  PROSPECTIVE: 3926,
};

export const formatProductPricingData = ({
  rawPricingData,
  shouldUsePricingService,
}) => {
  if (!rawPricingData) {
    return {};
  }
  if (shouldUsePricingService) {
    return rawPricingData?.reduce(
      (acc, productPricing) => ({
        ...acc,
        [productPricing.masterProductId]: {
          pricing: productPricing,
          retailUnitPrice: productPricing.retail,
          defaultUnitPrice: productPricing.final,
          outletUnitPrice: productPricing.outlet,
          promos: productPricing.finalDetails,
          promoCode: productPricing.finalDetails?.promoCode,
        },
      }),
      {}
    );
  }
  return rawPricingData?.products?.reduce(
    (acc, product) => ({
      ...acc,
      [product.masterProductId]: {
        pricing: product.pricing,
        retailUnitPrice: product.pricing?.retail,
        defaultUnitPrice: product.pricing?.final,
        outletUnitPrice: product.pricing?.outlet,
        promos: product.pricing?.finalDetails,
        promoCode: product.pricing?.finalDetails?.promoCode,
      },
    }),
    {}
  );
};

export const getGqlPricingMap = async ({
  products,
  apolloClient,
  isVip = false,
  shouldUsePricingService = false,
}) => {
  try {
    const productPricingVariables = shouldUsePricingService
      ? {
          masterProductIds: products.map((product) => product.masterProductId),
          membershipStatus: isVip
            ? MEMBERSHIP_STATUS.VIP
            : MEMBERSHIP_STATUS.PROSPECTIVE,
        }
      : getProductGqlQueryParameters({
          productListing: products,
          useRetailDiscounting: !isVip,
        });

    const { data } = await apolloClient.query({
      context: {
        clientName: 'supergraph',
        headers: {
          'x-datasource-version': shouldUsePricingService
            ? 'pricingService'
            : 'bff',
        },
      },
      query: shouldUsePricingService
        ? BASE_PRODUCTS_PRICING_QUERY_PRICING_SERVICE
        : BASE_PRODUCTS_PRICING_QUERY_BFF,
      variables: productPricingVariables,
      errorPolicy: 'all',
    });

    const rawPricingData = shouldUsePricingService
      ? data?.productPricing
      : data?.productListing;

    return formatProductPricingData({
      rawPricingData,
      shouldUsePricingService,
    });
  } catch (e) {
    return {};
  }
};

export const formatProductRelatedProductsListing = (
  productRelatedProductsListing
) => {
  if (!productRelatedProductsListing) {
    return {};
  }
  return productRelatedProductsListing?.products?.reduce((acc, product) => {
    // index the swatches by productId
    const swatchesMap = product.swatches.reduce((swAcc, swatch) => {
      swAcc[swatch.productId] = swatch.url;
      return swAcc;
    }, {});

    const updatedRelatedProducts = product.relatedProducts
      .map((relatedProduct) => {
        const productImages = relatedProduct.images;
        const tagIdList = relatedProduct.tags.map((tag) => tag.id);

        const newRelatedProduct = {
          ...relatedProduct,
          swatchUrl: swatchesMap[relatedProduct.masterProductId],
          productImages,
          tagIdList,
        };

        // don't needs tags array anymore after getting the tagIdList
        delete newRelatedProduct.tags;

        return newRelatedProduct;
      })
      .sort((a, b) => b.isSelf - a.isSelf);

    acc[product.masterProductId] = {
      relatedProductIdObjectList: updatedRelatedProducts,
      componentProductIdObjectList: product.componentProductIdObject,
    };

    return acc;
  }, {});
};

export const getGqlRelatedProductMap = async ({
  products,
  apolloClient,
  shouldShowWarehouseProducts,
}) => {
  try {
    const relatedProductsVariables = getProductGqlQueryParameters({
      productListing: products,
      shouldShowWarehouseProducts,
    });

    const {
      data: { productListing: productRelatedProductsListing },
    } = await apolloClient.query({
      context: { clientName: 'supergraph' },
      query: BASE_PRODUCTS_RELATED_PRODUCTS_QUERY,
      variables: relatedProductsVariables,
    });
    return formatProductRelatedProductsListing(productRelatedProductsListing);
  } catch (e) {
    return {};
  }
};
